import React, { useEffect, useState } from "react";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, Box, CircularProgress, Grid, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { clearOnboardingApplicationFlags, getOnboardingAppCompletedSuccessful, getPmDetails, getUpdateOnboardingApplication, selectOnboardingApplicationState } from "./sagas/pmonboardingSlice";
import { clearCredentials } from "../../shared/sagas/auth/authSlice";
import { makeStyles } from "@mui/styles";
import TimeIcon from '../../icons/Time.svg';
import { css } from "@emotion/react";
import { HashLoader } from "react-spinners";
import { OnboardingStepStatus, OnboardingStepType } from "@inosio/payments-sdk";
import { InosioImplementationCallUrl } from "../../utils/constants";

export const useStyles = makeStyles({
  globalError: {
    display: "flex",
    flexDirection: "row",
    marginBottom: "30px",
    borderTop: "2px  solid #DB3939",
    borderRadius: "24px",
    padding: "12px 20px",
    width: "fit-content",
    height: "49px",
  },
  accordionRoot: {
    boxShadow: 'none !important',
    borderTop: '1px solid #F1F0F5',
    borderBottom: '1px solid #F1F0F5',
    '&::before': {
      backgroundColor: 'transparent !important'
    },
    '&.Mui-expanded': {
      margin: '0 !important'
    },
    '&.Mui-disabled': {
      backgroundColor: '#ffffff !important'
    }
  },
  startBtn: {
    width: 183,
    height: 40,
    borderRadius: '24px !important',
    background: "#39A2DB !important",
    '&.Mui-disabled': {
      background: "#D3D3D3 !important",
    }
  },
  startBtnText: {
    fontFamily: 'Open Sans !important',
    fontStyle: 'normal',
    fontWeight: '700 !important',
    fontSize: '18px !important',
    lineHeight: '22px !important',
    color: '#FFFFFF !important',
    textTransform: 'capitalize',
  },
  justMeBtn: {
    width: 183,
    height: 40,
    borderRadius: '24px !important',
    background: '#ffffff !important',
    border: '1px solid #39A2DB !important',
    '&.Mui-disabled': {
      border: '1px solid #D3D3D3 !important',
      '& p': {
        color: '#D3D3D3 !important',
      }
    }
  },
  justMeBtnText: {
    fontFamily: 'Open Sans !important',
    fontStyle: 'normal',
    fontWeight: '700 !important',
    fontSize: '18px !important',
    lineHeight: '22px !important',
    color: '#39A2DB !important',
    textTransform: 'none',
  },
  wrapper: {
    margin: "1px",
    position: "relative",
    display: 'inline-block',
  },
  buttonProgress: {
    color: "#053742",
    position: "absolute",
    top: "10px",
    left: "calc( 50% - 11px )",
  },
  summaryText1: {
    fontFamily: 'Open Sans !important',
    fontWeight: '500 !important',
    fontSize: '18px !important',
    lineHeight: '24px !important',
    color: '#111827 !important'
  },
  summaryText2: {
    fontFamily: 'Open Sans !important',
    fontWeight: '500 !important',
    fontSize: '14px !important',
    lineHeight: '24px !important',
    color: '#DBAE39 !important',
    marginRight: '12px !important'
  },
  detailText1: {
    fontFamily: 'Open Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    fontSize: '16px !important',
    lineHeight: '22px !important',
    color: '#111827 !important',
    display: 'flex',
    alignItems: 'center'
  },
  detailText2: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    fontSize: '16px !important',
    lineHeight: '24px !important',
    color: '#6B7280 !important',
  },
  largeBtn: {
    color: "#fff !important",
    background: "#39A2DB !important",
    borderRadius: "24px !important",
    padding: "9px 17px !important",
    height: "49px",
    margin: '10px 36px !important',
    '&.Mui-disabled': {
      background: "#D3D3D3 !important",
    }
  },
})

const enum PageStates {
  // Pricing = 'Pricing',
  PaymentsProcessingApplication = 'Payments Processing Application',
  // AddIntegration = 'Add Integration',
  AddOtherUsers = 'Add Other Users',
  ImplementationCall = 'Implementation Call',
}

export const Wizard = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const [pageState, setPageState] = useState<PageStates | false>(false);
  const handleAccordionChange = (panel: PageStates) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setPageState(isExpanded ? panel : false);
  };

  const { pmDetails, onboarding, errorMessage, isFetching } = useSelector(selectOnboardingApplicationState);

  useEffect(() => {
    // Clear all onboarding flags
    dispatch({
      type: clearOnboardingApplicationFlags.type,
      payload: {},
    });

    // fetch company uuid if it doesnt exist in app context
    !onboarding?.Uuid &&
      dispatch({
        type: getPmDetails.type,
        payload: {},
      });
  }, []);

  useEffect(() => {
    // fetch onboarding application if it doesnt exist in app context
    pmDetails.Company?.Uuid &&
      !onboarding?.Uuid &&
      dispatch({
        type: getUpdateOnboardingApplication.type,
        payload: {
          company_uuid: pmDetails.Company.Uuid,
          data: {},
        },
      });
  }, [pmDetails]);

  const [initialLoad, setInitialLoad] = useState(false);
  useEffect(() => {
    if (!initialLoad && !pageState && onboarding?.Uuid) {
      if (onboarding?.PaymentsProcessingApplication !== OnboardingStepStatus.Completed) setPageState(PageStates.PaymentsProcessingApplication);
      if (onboarding?.AddOtherUsers !== OnboardingStepStatus.Completed) setPageState(PageStates.AddOtherUsers);
      if (onboarding?.ImplementationCall !== OnboardingStepStatus.Completed) setPageState(PageStates.ImplementationCall);

      // if (onboarding?.AddIntegration !== OnboardingStepStatus.Completed) setPageState(PageStates.AddIntegration);
      setInitialLoad(true);
    }
  }, [initialLoad, pageState, onboarding])

  //Handle Update pricing option
  // const handleUpdatePricing = () => {
  //   history.push("/pricing");
  // }

  //Handle start Payments Processing Application
  const handleStartPPA = () => {
    history.push("/onboarding/applicationwizard");
  }

  // const handleStartIntegration = () => {
  //   history.push("/pmui/integrations");
  // }

  const handleStartAddUser = () => {
    history.push("/pmui/accountManagement");
    dispatch({
      type: getUpdateOnboardingApplication.type,
      payload: {
        company_uuid: onboarding?.Uuid || pmDetails?.Company?.Uuid,
        data: { CompletedOnboardingStep: OnboardingStepType.AddOtherUsers },
      },
    });
  }

  //Handle It's just me in the step of Add Other Users
  const handleCompleteAddUser = () => {
    dispatch({
      type: getUpdateOnboardingApplication.type,
      payload: {
        company_uuid: onboarding?.Uuid || pmDetails?.Company?.Uuid,
        data: { CompletedOnboardingStep: OnboardingStepType.AddOtherUsers },
      },
    });
  }

  const handleImplementationCall = () => {
    dispatch({
      type: getUpdateOnboardingApplication.type,
      payload: {
        company_uuid: onboarding?.Uuid || pmDetails?.Company?.Uuid,
        data: { CompletedOnboardingStep: OnboardingStepType.ImplementationCall },
      },
    });
    window.open(InosioImplementationCallUrl, '_blank', 'noopener,noreferrer');
  }

  const isCompletedAllSteps = onboarding?.PaymentsProcessingApplication === OnboardingStepStatus.Completed && onboarding?.AddOtherUsers === OnboardingStepStatus.Completed && onboarding?.ImplementationCall === OnboardingStepStatus.Completed;

  const handleSaveAndFinishLater = () => {
    history.push("/pmui");
  }

  const handleContinue = () => {
    dispatch({
      type: getOnboardingAppCompletedSuccessful.type,
      payload: {},
    });
    history.push("/pmui");
  }

  const handleLogout = () => {
    dispatch(clearCredentials());
    history.push("/login");
  };

  const override = css`
    display: block;
    margin: 100px auto;
  `;

  return (
    <Grid item xs={11} sm={9} lg={8} xl={7.5}>
      {onboarding?.Uuid ? (
        <React.Fragment>
          {errorMessage?.length ? (
            <div className={classes.globalError}>
              <Typography
                style={{
                  fontFamily: "BMWHelvetica",
                  fontStyle: "normal",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "18px",
                  color: "#DB3939",
                }}
              >
                {`Something went wrong. Please try again later or contact support.`}
              </Typography>
            </div>
          ) : (
            <></>
          )}

          <Typography
            style={{
              fontFamily: 'Open Sans',
              fontWeight: 700,
              fontSize: '18px',
              lineHeight: '23px',
              color: '#14142B',
              marginBottom: '18px'
            }}
          >
            {!isCompletedAllSteps ? "Congratulations🎉 Your account has been created. Please complete the following steps to finish your set up." : "You're all set. We'll take it from here. See you at the implementation meeting!"}
          </Typography>

          {/* <Accordion classes={{ root: classes.accordionRoot }} sx={{ borderBottom: 'none' }} expanded={pageState === PageStates.Pricing} onChange={handleAccordionChange(PageStates.Pricing)}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel0bh-content"
              id="panel0bh-header"
            >
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', margin: '2px 0' }}>
                <Typography className={ classes.summaryText1 }>Pricing</Typography>
                
                <Typography className={ classes.summaryText2 } sx={{ color: '#39DB8D !important' }}>
                  {'Completed'}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0 !important' }}>
              <Box sx={{ padding: '0 32px' }}>
                <Typography style={{ marginTop: "5px", marginBottom: '13px' }} className={ classes.detailText1 }>
                  <img src={TimeIcon} alt="" style={{ marginRight: '8px' }} />1-2 minutes
                </Typography>

                <Typography style={{ marginBottom: '18px' }} className={ classes.detailText2 }>
                  Select your Inosio plan and pricing options. It can always be updated or changed later if/when needed.
                </Typography>

                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '31px' }}>
                  <Button variant="contained" className={classes.startBtn} onClick={handleUpdatePricing}>
                    <Typography className={classes.startBtnText}>
                      Update Now
                    </Typography>
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion> */}

          {/* <Accordion classes={{ root: classes.accordionRoot }} sx={{ borderBottom: 'none' }} expanded={pageState === PageStates.AddIntegration} onChange={handleAccordionChange(PageStates.AddIntegration)}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2bh-content"
              id="panel2bh-header"
            >
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', margin: '2px 0' }}>
                <Typography className={ classes.summaryText1 }>Add Integration</Typography>
                
                <Typography className={ classes.summaryText2 } 
                  sx={{ color: onboarding?.AddIntegration === OnboardingStepStatus.Completed ? '#39DB8D !important': '' }}
                >
                  {onboarding?.AddIntegration === OnboardingStepStatus.Completed ? 'Completed': 'Incomplete'}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', justifyContent: 'center', padding: '0 !important' }}>
              <Box sx={{ padding: '0 32px' }}>
                <Typography style={{ marginTop: "5px", marginBottom: '13px' }} className={ classes.detailText1 }>
                  <img src={TimeIcon} alt="" style={{ marginRight: '8px' }} />5-10 minutes
                </Typography>

                <Typography style={{ marginBottom: '13px' }} className={ classes.detailText2 }>
                  Add an accounting software integration to import properties, leases, residents and bank accounts. This will also ensure that Inosio can properly push payment data back to your accounting software.
                </Typography>

                <Typography style={{ marginBottom: '8px' }} className={ classes.detailText1 }>Needed</Typography>

                <Typography style={{ marginBottom: '18px' }} className={ classes.detailText2 }>
                  Admin access to your computer and Inosio
                </Typography>

                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '31px' }}>
                  <Button disabled={onboarding?.AddIntegration === OnboardingStepStatus.Completed} variant="contained" className={classes.startBtn} onClick={handleStartIntegration}>
                    <Typography className={classes.startBtnText}>
                      Start Now
                    </Typography>
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion> */}

          <Accordion classes={{ root: classes.accordionRoot }} sx={{ borderBottom: 'none' }} expanded={pageState === PageStates.ImplementationCall} onChange={handleAccordionChange(PageStates.ImplementationCall)}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel4bh-content"
              id="panel4bh-header"
            >
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', margin: '2px 0' }}>
                <Typography className={ classes.summaryText1 }>Implementation Call</Typography>
                
                <Typography className={ classes.summaryText2 } 
                  sx={{ color: onboarding?.ImplementationCall === OnboardingStepStatus.Completed ? '#39DB8D !important': '' }}
                >
                  {onboarding?.ImplementationCall === OnboardingStepStatus.Completed ? 'Completed': 'Incomplete'}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', justifyContent: 'center', padding: '0 !important' }}>
              <Box sx={{ padding: '0 32px' }}>
                <Typography style={{ marginTop: "5px", marginBottom: '13px' }} className={ classes.detailText1 }>
                  <img src={TimeIcon} alt="" style={{ marginRight: '8px' }} />90 minutes
                </Typography>

                <Typography style={{ marginBottom: '13px' }} className={ classes.detailText2 }>
                  Schedule an implementation call to configure your account, ensure the integration is working properly, and discuss a go-live plan.
                </Typography>

                <Typography style={{ marginBottom: '8px' }} className={ classes.detailText1 }>Needed</Typography>

                <Typography style={{ marginBottom: '8px' }} className={ classes.detailText2 }>
                  You will need to have the latest version of Promas installed prior to this call.
                </Typography>

                <Typography style={{ marginBottom: '18px' }} className={ classes.detailText2 }>
                  Complete all other set up tasks.
                </Typography>

                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '31px' }}>
                  <Button variant="contained" className={classes.startBtn} onClick={handleImplementationCall}>
                    <Typography className={classes.startBtnText}>
                      Schedule Now
                    </Typography>
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion classes={{ root: classes.accordionRoot }} sx={{ borderBottom: 'none' }} expanded={pageState === PageStates.AddOtherUsers} onChange={handleAccordionChange(PageStates.AddOtherUsers)}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel3bh-content"
              id="panel3bh-header"
            >
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', margin: '2px 0' }}>
                <Typography className={ classes.summaryText1 }>Add Other Users</Typography>
                
                <Typography className={ classes.summaryText2 } 
                  sx={{ color: onboarding?.AddOtherUsers === OnboardingStepStatus.Completed ? '#39DB8D !important': '' }}
                >
                  {onboarding?.AddOtherUsers === OnboardingStepStatus.Completed ? 'Completed': 'Incomplete'}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', justifyContent: 'center', padding: '0 !important' }}>
              <Box sx={{ padding: '0 32px' }}>
                <Typography style={{ marginTop: "5px", marginBottom: '13px' }} className={ classes.detailText1 }>
                  <img src={TimeIcon} alt="" style={{ marginRight: '8px' }} />5-10 minutes
                </Typography>

                <Typography style={{ marginBottom: '13px' }} className={ classes.detailText2 }>
                  Add other staff members to your account. Each person should have their own account. There's no additional charge!
                </Typography>

                <Typography style={{ marginBottom: '8px' }} className={ classes.detailText1 }>Needed</Typography>

                <Typography style={{ marginBottom: '8px' }} className={ classes.detailText2 }>
                  Email addresses
                </Typography>

                <Typography style={{ marginBottom: '18px' }} className={ classes.detailText2 }>
                  Staff names
                </Typography>

                <Box sx={{ width: '100%', textAlign: 'center' }}>
                  <div style={{ marginBottom: '10px' }}>
                    <div className={classes.wrapper}>
                      <Button disabled={onboarding?.AddOtherUsers === OnboardingStepStatus.Completed} className={classes.justMeBtn} sx={{ margin: '0 10px 20px' }} onClick={handleCompleteAddUser}>
                        <Typography className={classes.justMeBtnText}>
                          It's just me
                        </Typography>
                      </Button>
                      {isFetching && <CircularProgress size={22} className={classes.buttonProgress} />}
                    </div>

                    <Button disabled={onboarding?.AddOtherUsers === OnboardingStepStatus.Completed} variant="contained" className={classes.startBtn} sx={{ margin: '0 10px 20px' }} onClick={handleStartAddUser}>
                      <Typography className={classes.startBtnText}>
                        Start Now
                      </Typography>
                    </Button>
                  </div>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion classes={{ root: classes.accordionRoot }} expanded={pageState === PageStates.PaymentsProcessingApplication} onChange={handleAccordionChange(PageStates.PaymentsProcessingApplication)}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', margin: '2px 0' }}>
                <Typography className={ classes.summaryText1 }>Payments Processing Application</Typography>
                
                <Typography className={ classes.summaryText2 } 
                  sx={{ color: onboarding?.PaymentsProcessingApplication === OnboardingStepStatus.Completed ? '#39DB8D !important': '' }}
                >
                  {onboarding?.PaymentsProcessingApplication === OnboardingStepStatus.Completed ? 'Completed': 'Incomplete'}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0 !important' }}>
              <Box sx={{ padding: '0 32px' }}>
                <Typography style={{ marginTop: "5px", marginBottom: '13px' }} className={ classes.detailText1 }>
                  <img src={TimeIcon} alt="" style={{ marginRight: '8px' }} />5-10 minutes
                </Typography>

                <Typography style={{ marginBottom: '13px' }} className={ classes.detailText2 }>
                  Completing this application is required before processing payments.
                </Typography>

                <Typography style={{ marginBottom: '8px' }} className={ classes.detailText1 }>Needed</Typography>

                <Typography style={{ marginBottom: '8px' }} className={ classes.detailText2 }>
                  Company details Including tax ID
                </Typography>

                <Typography style={{ marginBottom: '18px' }} className={ classes.detailText2 }>
                  Personal details about each owner including DOB, SSN and physical address
                </Typography>

                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '31px' }}>
                  <Button variant="contained" className={classes.startBtn} onClick={handleStartPPA}>
                    <Typography className={classes.startBtnText}>
                      {onboarding?.PaymentsProcessingApplication === OnboardingStepStatus.Completed ? 'Update Now': 'Start Now'}
                    </Typography>
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Box sx={{ textAlign: 'center', borderBottom: '1px solid #F1F0F5', padding: '36px 22px 14px' }}>
            <div>
              <Button className={classes.largeBtn} sx={{ background: '#A2DBFA !important' }} onClick={handleSaveAndFinishLater}>
                <Typography className={classes.startBtnText} sx={{ fontWeight: '1000 !important', fontFamily: 'Work Sans !important' }}>
                  {`Save & Finish Later`}
                </Typography>
              </Button>

              <Button disabled={!isCompletedAllSteps} className={classes.largeBtn} onClick={handleContinue}>
                <Typography className={classes.startBtnText} sx={{ fontWeight: '600 !important', fontFamily: 'Work Sans !important' }}>
                  Continue
                </Typography>
              </Button>
            </div>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: 'Work Sans',
                fontWeight: 500,
                fontSize: '18px',
                lineHeight: '22px',
                color: '#39A2DB',
                margin: '24px 0',
                "&:hover": {
                  cursor: "pointer",
                },
                display: 'inline-block'
              }}
              onClick={handleLogout}
            >
              Log out
            </Typography>
          </Box>
        </React.Fragment>
      ) : (
        <Box sx={{ width: '100%' }}>
          <HashLoader color={"#D3D3D3"} loading={true} size={150} css={override} />
        </Box>
      )}
    </Grid>
  );
};
